import { daysUntilDue, formatCurrency, formatDate, openAmount } from '../buchhaltung/invoiceUtils'

/**
 * Fachlogik der Seite Ausgangsrechnungen. Formatierer, Skonto-Rechnung und
 * offener Betrag kommen aus buchhaltung/invoiceUtils — hier steht nur, was es
 * nur bei Ausgangsrechnungen gibt: Zahlungseingang statt Zahlungsausgang.
 */

export type OutgoingState = 'open' | 'overdue' | 'paid' | 'cancelled'

export interface OutgoingRow {
  id: number
  status?: string | null
  invoice_number?: string | null
  supplier_name?: string | null
  recipient_name?: string | null
  invoice_date?: string | null
  due_date?: string | null
  gross_amount?: number | null
  paid_amount?: number | null
  paid_at?: string | null
  skonto_percent?: number | null
  skonto_amount?: number | null
  skonto_date?: string | null
}

const CLOSED = ['paid', 'resolved', 'rejected']

/** Lokales Datum als YYYY-MM-DD — NIE toISOString(), das ist nachts UTC-gestern. */
export function localIsoDate(d: Date = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Tage zu einem YYYY-MM-DD addieren (Mittag, damit die Zeitumstellung keinen Tag schluckt). */
export function addDaysIso(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00`)
  d.setDate(d.getDate() + days)
  return localIsoDate(d)
}

/** Ganze Tage zwischen zwei YYYY-MM-DD. */
export function daysBetweenIso(from: string, to: string): number {
  return Math.round((new Date(`${to}T12:00:00`).getTime() - new Date(`${from}T12:00:00`).getTime()) / 86_400_000)
}

export const customerName = (inv: OutgoingRow) => inv.recipient_name || inv.supplier_name || '-'

/** Dieselbe Abgrenzung wie der Filter status=overdue im Backend (getAllInvoices). */
export function outgoingState(inv: OutgoingRow): OutgoingState {
  // Abgelehnt/storniert ist NICHT bezahlt — Kachel und Filter "Bezahlt" zaehlen nur status='paid'
  if (inv.status === 'rejected') return 'cancelled'
  if (CLOSED.includes(inv.status ?? '')) return 'paid'
  const days = daysUntilDue(inv.due_date)
  return days !== null && days < 0 ? 'overdue' : 'open'
}

export const isOpenState = (s: OutgoingState) => s === 'open' || s === 'overdue'

export const STATE_CONFIG: Record<OutgoingState, { label: string; color: string }> = {
  open:    { label: 'Offen',      color: 'bg-amber-100 text-amber-700' },
  overdue: { label: 'Überfällig', color: 'bg-red-100 text-red-700' },
  paid:    { label: 'Bezahlt',    color: 'bg-green-200 text-green-800' },
  cancelled: { label: 'Abgelehnt', color: 'bg-gray-100 text-gray-500' },
}

export type DueTone = 'over' | 'red' | 'amber' | 'green' | null

/** "Fällig in 5 Tagen" / "Fällig heute" / "3 Tage überfällig" — nur für offene Rechnungen. */
export function dueLabel(inv: OutgoingRow): { text: string; tone: DueTone } {
  if (CLOSED.includes(inv.status ?? '')) return { text: '', tone: null }
  const days = daysUntilDue(inv.due_date)
  if (days === null) return { text: 'Kein Zahlungsziel', tone: null }
  if (days < 0) return { text: days === -1 ? '1 Tag überfällig' : `${-days} Tage überfällig`, tone: 'over' }
  if (days === 0) return { text: 'Fällig heute', tone: 'red' }
  if (days === 1) return { text: 'Fällig morgen', tone: 'red' }
  return { text: `Fällig in ${days} Tagen`, tone: days <= 7 ? 'amber' : 'green' }
}

/** Skonto hinterlegt? Gleiche Regel wie das Backend: ohne Satz kein Skonto. */
export const hasSkonto = (inv: OutgoingRow) => !!inv.skonto_percent

/** Laeuft die Skontofrist am Stichtag (Default heute) noch? */
export function skontoValidOn(inv: OutgoingRow, isoDay: string = localIsoDate()): boolean {
  return hasSkonto(inv) && !!inv.skonto_date && inv.skonto_date >= isoDay && (inv.skonto_amount ?? 0) > 0
}

/** "2 % · 12,34 € bis 12.11.26" */
export function skontoLabel(inv: OutgoingRow): string | null {
  if (!hasSkonto(inv)) return null
  const parts = [`${String(inv.skonto_percent).replace('.', ',')} %`]
  if (inv.skonto_amount != null) parts.push(formatCurrency(inv.skonto_amount))
  return parts.join(' · ') + (inv.skonto_date ? ` bis ${formatDate(inv.skonto_date)}` : '')
}

/** Offener Betrag (Brutto − Teilzahlungen). Ausgangsrechnungen haben keine Gutschriften-Verrechnung. */
export const outgoingOpenAmount = (inv: OutgoingRow) => openAmount(inv as any)

/** Was eingeht, wenn der Kunde zum Stichtag zahlt — mit oder ohne gezogenes Skonto. */
export function expectedPayment(inv: OutgoingRow, withSkonto: boolean): number {
  const open = outgoingOpenAmount(inv)
  const skonto = withSkonto ? Math.round((inv.skonto_amount || 0) * 100) / 100 : 0
  return Math.round((open - skonto) * 100) / 100
}
